import { useState, useEffect, useRef, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Image } from "primereact/image";
import { FileUpload } from "primereact/fileupload";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import AppToast from "../../../../shared/components/AppToast";
import { ensureCatalogAuth } from "../../../../services/backend/authService";
import {
  getFotosProductosByCodigo,
  insertarFotoProducto,
  editarFotoProducto,
  eliminarFotoProducto,
  UpdatePortada,
} from "../../../../services/backend/productoService";
import logger from "../../../../shared/utils/logger";

export const FotosProductoDialog = ({ visible, onHide, producto }) => {
  const toastRef = useRef(null);
  const insertFileUploadRef = useRef(null);
  const editFileInputRef = useRef(null);

  const [fotos, setFotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editFoto, setEditFoto] = useState(null);
  const [editPreview, setEditPreview] = useState(null);
  const [fotoEliminar, setFotoEliminar] = useState(null);
  const [eliminando, setEliminando] = useState(false);

  const codigo = producto?.codigo || "";

  const fetchFotos = useCallback(async () => {
    if (!codigo) {
      setFotos([]);
      return;
    }
    setLoading(true);
    try {
      await ensureCatalogAuth();
      const res = await getFotosProductosByCodigo(codigo);
      if (res?.error) {
        setFotos([]);
      } else {
        const items = Array.isArray(res?.fotos)
          ? res.fotos
          : Array.isArray(res)
          ? res
          : [];
        setFotos(items);
      }
    } catch (err) {
      logger.error("Error al cargar fotos del producto:", err);
      setFotos([]);
    } finally {
      setLoading(false);
    }
  }, [codigo]);

  useEffect(() => {
    if (visible && codigo) {
      setEditId(null);
      setEditFoto(null);
      setEditPreview(null);
      insertFileUploadRef.current?.clear();
      if (editFileInputRef.current) editFileInputRef.current.value = "";
      fetchFotos();
    }
  }, [visible, codigo, fetchFotos]);

  const handleInsertUpload = async ({ files }) => {
    if (!files?.length) return;
    setSaving(true);
    try {
      await ensureCatalogAuth();
      for (const file of files) {
        const formData = new FormData();
        formData.append("codigo", codigo);
        formData.append("mime", file.type || "image/jpeg");
        formData.append("foto", file);
        await insertarFotoProducto(formData);
      }
      insertFileUploadRef.current?.clear();
      toastRef.current?.show({
        severity: "success",
        summary: "Creada",
        detail: `${files.length} foto(s) agregada(s) correctamente.`,
        life: 3000,
      });
      await fetchFotos();
    } catch (err) {
      logger.error("Error al insertar foto:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudieron agregar las fotos.",
        life: 4000,
      });
      insertFileUploadRef.current?.clear();
    } finally {
      setSaving(false);
    }
  };

  const handleEditFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setEditFoto(file);
    setEditPreview(file ? URL.createObjectURL(file) : null);
  };

  const handleEditar = async (foto) => {
    if (!editFoto) {
      toastRef.current?.show({
        severity: "warn",
        summary: "Sin archivo",
        detail: "Seleccione una nueva imagen.",
        life: 3000,
      });
      return;
    }
    setSaving(true);
    try {
      await ensureCatalogAuth();
      const formData = new FormData();
      formData.append("id", String(foto.id));
      formData.append("codigo", codigo);
      formData.append("mime", editFoto.type || "image/jpeg");
      formData.append("foto", editFoto);
      formData.append("portada", foto.portada ? "true" : "false");
      await editarFotoProducto(formData);
      cancelEdit();
      toastRef.current?.show({
        severity: "success",
        summary: "Actualizada",
        detail: "Foto actualizada correctamente.",
        life: 3000,
      });
      await fetchFotos();
    } catch (err) {
      logger.error("Error al editar foto:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo actualizar la foto.",
        life: 4000,
      });
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (foto) => {
    setEditId(foto.id);
    setEditFoto(null);
    setEditPreview(`data:${foto.mime};base64,${foto.foto}`);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditFoto(null);
    setEditPreview(null);
    if (editFileInputRef.current) editFileInputRef.current.value = "";
  };

  const handleEliminar = (foto) => {
    setFotoEliminar(foto);
  };

  const confirmarEliminar = async () => {
    const foto = fotoEliminar;
    if (!foto) return;
    setEliminando(true);
    setBusyId(foto.id);
    try {
      await ensureCatalogAuth();
      await eliminarFotoProducto(foto.id);
      if (editId === foto.id) cancelEdit();
      toastRef.current?.show({
        severity: "success",
        summary: "Eliminada",
        detail: "Foto eliminada correctamente.",
        life: 3000,
      });
      await fetchFotos();
    } catch (err) {
      logger.error("Error al eliminar foto:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo eliminar la foto.",
        life: 4000,
      });
    } finally {
      setBusyId(null);
      setEliminando(false);
      setFotoEliminar(null);
    }
  };

  const cancelarEliminar = () => setFotoEliminar(null);

  const handlePortada = async (foto) => {
    if (foto.portada) return;
    setBusyId(foto.id);
    try {
      await ensureCatalogAuth();
      await UpdatePortada(foto.id, codigo);
      toastRef.current?.show({
        severity: "success",
        summary: "Portada",
        detail: "Foto marcada como portada.",
        life: 3000,
      });
      await fetchFotos();
    } catch (err) {
      logger.error("Error al marcar portada:", err);
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: "No se pudo marcar como portada.",
        life: 4000,
      });
    } finally {
      setBusyId(null);
    }
  };

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button className="p-button-text" label="Cerrar" onClick={onHide} />
    </div>
  );

  return (
    <Dialog
      header={`Fotos de ${producto?.descripcion}`}
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "60rem", maxWidth: "95vw" }}
    >
      <AppToast ref={toastRef} />

      <ConfirmDialog
        visible={!!fotoEliminar}
        header="Eliminar Foto"
        message="¿Eliminar esta foto?"
        acceptLabel="Eliminar"
        rejectLabel="Cancelar"
        acceptClassName="p-button-danger"
        loading={eliminando}
        onCancel={cancelarEliminar}
        onConfirm={confirmarEliminar}
      />

      <div className="pt-3">
        <div className="field mb-4">
          <label className="block font-medium mb-2">Agregar nuevas fotos</label>
          <FileUpload
            ref={insertFileUploadRef}
            customUpload
            uploadHandler={handleInsertUpload}
            multiple
            accept=".jpg,.jpeg"
            emptyTemplate={
              <p className="m-0">
                Arrastra y suelta fotos .jpg o jpeg para subirlas.
              </p>
            }
            chooseLabel="Seleccionar"
            uploadLabel="Subir"
            cancelLabel="Cancelar"
            disabled={saving}
          />
        </div>

        <div className="field">
          <label className="block font-medium mb-2">
            Fotos existentes {loading ? "(cargando…)" : `(${fotos.length})`}
          </label>
          {fotos.length === 0 && !loading ? (
            <div className="text-500 text-sm">
              Este producto aún no tiene fotos.
            </div>
          ) : (
            <div className="grid">
              {fotos.map((foto) => {
                const src = `data:${foto.mime};base64,${foto.foto}`;
                const isEditing = editId === foto.id;
                return (
                  <div key={foto.id} className="col-12 md:col-4">
                    <div className="border-1 border-300 border-round p-2 flex flex-column gap-2">
                      <div className="relative">
                        <Image
                          src={src}
                          alt={`Foto ${foto.id}`}
                          preview
                          className="w-full border-round bg-gray-200"
                          imageClassName="w-full"
                          imageStyle={{ height: "150px", objectFit: "contain" }}
                        />
                        {foto.portada && (
                          <span className="absolute top-0 left-0 m-1 px-2 py-1 border-round text-xs font-semibold bg-green-500 text-white opacity-90">
                            Portada
                          </span>
                        )}
                      </div>

                      {!isEditing ? (
                        <div className="flex align-items-center justify-content-between">
                          <Button
                            icon="pi pi-star"
                            label="Portada"
                            outlined
                            severity={foto.portada ? "success" : "secondary"}
                            disabled={foto.portada || busyId === foto.id}
                            onClick={() => handlePortada(foto)}
                            size="small"
                            tooltip="Marcar como portada"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-pencil"
                            outlined
        
                            disabled={busyId === foto.id}
                            onClick={() => startEdit(foto)}
                            size="small"
                            tooltip="Editar"
                            tooltipOptions={{ position: "top" }}
                          />
                          <Button
                            icon="pi pi-trash"
                            outlined
                            severity="danger"
                            disabled={busyId === foto.id}
                            loading={busyId === foto.id}
                            onClick={() => handleEliminar(foto)}
                            size="small"
                            tooltip="Eliminar"
                            tooltipOptions={{ position: "top" }}
                          />
                        </div>
                      ) : (
                        <div className="flex flex-column gap-2">
                          <input
                            ref={editFileInputRef}
                            type="file"
                            accept=".jpg,.jpeg"
                            onChange={handleEditFileChange}
                            className="w-full"
                          />
                          {editPreview && (
                            <Image
                              src={editPreview}
                              alt="Preview"
                              className="border-round"
                              imageClassName="w-full"
                              imageStyle={{ maxHeight: "120px", objectFit: "contain" }}
                            />
                          )}
                          <div className="flex gap-1">
                            <Button
                              icon="pi pi-save"
                              label="Guardar"
                              className="p-button-sm p-button-success"
                              loading={saving}
                              disabled={saving || !editFoto}
                              onClick={() => handleEditar(foto)}
                            />
                            <Button
                              icon="pi pi-times"
                              label="Cancelar"
                              className="p-button-sm p-button-text"
                              onClick={cancelEdit}
                              disabled={saving}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default FotosProductoDialog;
